/**
 * TopicHygiene — Cortex Hardening admin UI.
 * Lista red-flags ativas (24h) e blacklists permanentes do grupo,
 * com ação de remover (apenas admin do grupo). Read-only para não-admin.
 */
import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ShieldAlert, ShieldCheck, Trash2, RefreshCw } from "lucide-react";
import { toast } from "sonner";

interface Redflag {
  id: string;
  group_id: string;
  topic_label: string;
  created_at: string;
  expires_at: string;
  active: boolean;
}
interface Blacklist {
  id: string;
  group_id: string;
  topic_label: string;
  reason: string | null;
  created_at: string;
  active: boolean;
}

export default function TopicHygiene() {
  const [groupId, setGroupId] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [redflags, setRedflags] = useState<Redflag[]>([]);
  const [blacklists, setBlacklists] = useState<Blacklist[]>([]);

  async function load() {
    if (!groupId.trim()) { setRedflags([]); setBlacklists([]); return; }
    setLoading(true);
    try {
      const [{ data: rf }, { data: bl }] = await Promise.all([
        (supabase as any).from("working_memory_redflags")
          .select("id, group_id, topic_label, created_at, expires_at, active")
          .eq("group_id", groupId.trim())
          .eq("active", true)
          .gt("expires_at", new Date().toISOString())
          .order("created_at", { ascending: false }),
        (supabase as any).from("group_topic_blacklist")
          .select("id, group_id, topic_label, reason, created_at, active")
          .eq("group_id", groupId.trim())
          .eq("active", true)
          .order("created_at", { ascending: false }),
      ]);
      setRedflags((rf ?? []) as Redflag[]);
      setBlacklists((bl ?? []) as Blacklist[]);
    } catch (e) {
      toast.error("Erro carregando hygiene: " + (e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  async function clearRedflag(id: string) {
    try {
      const { error } = await (supabase as any).functions.invoke("topic-redflag-clear", {
        body: { redflag_id: id, group_id: groupId.trim() },
      });
      if (error) throw error;
      toast.success("Red-flag removida");
      load();
    } catch (e) {
      toast.error((e as Error).message);
    }
  }

  async function unblacklist(topic: string) {
    try {
      const { error } = await (supabase as any).functions.invoke("topic-unblacklist", {
        body: { topic_label: topic, group_id: groupId.trim() },
      });
      if (error) throw error;
      toast.success(`"${topic}" liberado`);
      load();
    } catch (e) {
      toast.error((e as Error).message);
    }
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <ShieldCheck className="h-4 w-4" /> Topic Hygiene
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            Tópicos que SYRA está silenciando neste grupo. Red-flags expiram em 24h; blacklists são permanentes (admin-only).
          </p>
        </CardHeader>
        <CardContent className="flex gap-2 items-end flex-wrap">
          <div className="flex-1 min-w-[260px]">
            <Label className="text-xs">group_id</Label>
            <Input value={groupId} onChange={(e) => setGroupId(e.target.value)} placeholder="UUID do grupo" />
          </div>
          <Button onClick={load} disabled={loading || !groupId.trim()}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Carregar
          </Button>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <ShieldAlert className="h-4 w-4 text-warning" /> Red-flags ativas ({redflags.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {redflags.length === 0 ? (
              <p className="text-xs text-muted-foreground">Nenhuma red-flag ativa.</p>
            ) : (
              <ul className="space-y-2">
                {redflags.map((r) => (
                  <li key={r.id} className="flex items-center justify-between gap-2 border-b border-border/50 pb-2 text-xs">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{r.topic_label}</div>
                      <div className="text-muted-foreground">expira {new Date(r.expires_at).toLocaleString("pt-BR")}</div>
                    </div>
                    <Button size="sm" variant="ghost" onClick={() => clearRedflag(r.id)}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-destructive" /> Blacklist permanente ({blacklists.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {blacklists.length === 0 ? (
              <p className="text-xs text-muted-foreground">Nenhum tópico bloqueado.</p>
            ) : (
              <ul className="space-y-2">
                {blacklists.map((b) => (
                  <li key={b.id} className="flex items-center justify-between gap-2 border-b border-border/50 pb-2 text-xs">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{b.topic_label}</div>
                      <div className="text-muted-foreground">
                        {b.reason ?? "sem motivo"} · {new Date(b.created_at).toLocaleDateString("pt-BR")}
                      </div>
                    </div>
                    <Badge variant="outline" className="text-[10px]">permanente</Badge>
                    <Button size="sm" variant="ghost" onClick={() => unblacklist(b.topic_label)}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
